import { createContext } from "react";
import type { DecisionPolicyConfig } from "@cometchat-ai/core";

export type { DecisionPolicyConfig } from "@cometchat-ai/core";

/** One selectable action of a multi-action (composio/native) tool. */
export interface SkillActionOption {
  slug: string;
  name: string;
}

/**
 * Bridges the (presentational) skill chip to the dashboard's per-agent tool
 * config. The editor stays controlled over a plain string; this side-channel
 * carries the staged action subset that the chip dialog edits and the agent
 * form persists (applied to `AgentSkill.configOverrideJsonb` at publish).
 */
export interface SkillActionsApi {
  /** Enabled-action universe for a skill slug; empty ⇒ not a multi-action tool. */
  getActions: (slug: string) => SkillActionOption[];
  /** Staged subset for a skill; `undefined` ⇒ all enabled actions (the default). */
  getSelected: (slug: string) => string[] | undefined;
  /** Replace the staged subset for a skill. */
  setSelected: (slug: string, actions: string[]) => void;
  /** The skill's type (composio/native/request_decision/…) for chip branching. */
  getSkillType?: (slug: string) => string | undefined;
  /** Staged decision policy for a `request_decision` skill; `undefined` ⇒ defaults. */
  getConfig?: (slug: string) => DecisionPolicyConfig | undefined;
  /** Replace the staged decision policy for a `request_decision` skill. */
  setConfig?: (slug: string, config: DecisionPolicyConfig) => void;
  /** When true the chip dialog is read-only (e.g. viewing a published version). */
  readOnly?: boolean;
}

export const SkillActionsContext = createContext<SkillActionsApi | null>(null);
